export interface WarmupMessage {
  text: string;
  dots?: boolean; // animated dots — only for loading state messages
}

// ─── Loading states ──────────────────────────────────────────────────────────
const LOADING: WarmupMessage[] = [
  { text: "Preparing your workspace", dots: true },
  { text: "Connecting to your team", dots: true },
  { text: "Loading your configuration", dots: true },
  { text: "Setting up your dashboard", dots: true },
  { text: "Loading admin workspace", dots: true },
  { text: "Syncing your settings", dots: true },
  { text: "Almost there", dots: true },
  { text: "Loading the latest features", dots: true },
  { text: "Getting the latest updates", dots: true },
  { text: "Loading your command center", dots: true },
  { text: "Finalizing setup", dots: true },
  { text: "Initializing your experience", dots: true },
  { text: "Checking your permissions", dots: true },
];

// ─── Platform tips ───────────────────────────────────────────────────────────
const PLATFORM_TIPS: WarmupMessage[] = [
  { text: "Tip: Workflows can automate your most repetitive tasks" },
  { text: "Tip: Set up alerts before queue times get too long" },
  { text: "Tip: Use custom fields to capture key customer data" },
  { text: "Tip: Surveys help measure customer satisfaction after every chat" },
  { text: "Tip: Transfer conversations to the right agent in seconds" },
  { text: "Tip: Canned responses let agents reply in one click" },
  { text: "Tip: Check reports to track your team's performance" },
  { text: "Tip: Routing rules assign chats by skill or availability" },
  { text: "Tip: SMS and voice channels are just a click away" },
  { text: "Tip: Proactive chat can increase conversions by 30%" },
  { text: "Tip: Monitor live queues in the Operations Center" },
  { text: "Tip: Enable bot automation to handle off-hours traffic" },
  { text: "Tip: Use shadow mode to coach agents in real time" },
  { text: "Tip: CRM integrations sync customer data automatically" },
  { text: "Tip: Schedule adherence reports track agent punctuality" },
  { text: "Tip: Set SLA targets to keep wait times in check" },
  { text: "Tip: Use the contact timeline to see the full history" },
  { text: "Tip: Dark mode is available in your profile settings" },
  { text: "Tip: Configure team schedules for accurate availability" },
  { text: "Tip: Group agents by skill for smarter routing" },
  { text: "Tip: Agent-to-agent threads let teams collaborate on hard cases" },
  { text: "Tip: Auto-resolve keeps your inbox clean" },
  { text: "Tip: Queue alerts fire before wait times get critical" },
  { text: "Tip: Custom reports let you track exactly what matters" },
  { text: "Tip: Test bot flows before going live" },
  { text: "Tip: Check your service level reports weekly" },
  { text: "Tip: Set up office hours to manage availability" },
  { text: "Tip: Track resolution time to find bottlenecks" },
  { text: "Tip: Monitor bot escalation rates to improve your flows" },
  { text: "Tip: Webhook events connect your workspace to other tools" },
  { text: "Tip: Set up multiple teams to handle different products or regions" },
  { text: "Tip: Use survey triggers after resolution for automatic CSAT collection" },
  { text: "Tip: Check missed chat reports to recover lost opportunities" },
  { text: "Tip: Agent availability reports help you staff smarter" },
  { text: "Tip: Use the contact search to look up any customer's full history" },
  { text: "Tip: Invite teammates — the more agents, the shorter the queue" },
  { text: "Tip: Use conversation labels for easier filtering" },
  { text: "Tip: Use routing rules to send VIP customers to senior agents" },
  { text: "Tip: CSAT scores update in real time on the Operations Center" },
  { text: "Tip: Create multiple bot flows for different entry points" },
  { text: "Tip: Role-based access keeps admin tools in the right hands" },
  { text: "Tip: Use the API to build custom integrations" },
  { text: "Tip: Add knowledge base articles so your AI bot can answer customer questions faster" },
  { text: "Tip: Audit logs track every configuration change your team makes" },
  { text: "Tip: AI bots can qualify leads before handing off to an agent" },
  { text: "Tip: Export reports to CSV for deeper analysis in your BI tools" },
  { text: "Tip: Channel-aware workflows let bots behave differently on each channel" },
  { text: "Tip: Set up proactive triggers based on time spent on a page" },
  { text: "Tip: Knowledge base articles feed directly into your AI bot's answers" },
  { text: "Tip: Use conversation variables to personalize every bot interaction" },
  { text: "Tip: BigCommerce and Shopify integrations unlock commerce-aware bots" },
  { text: "Tip: Multi-step workflows can handle complex scenarios without an agent" },
  { text: "Tip: Tag conversations by topic to spot your most common support themes" },
  { text: "Tip: Pin your most-used pages in the sidebar for faster navigation" },
  { text: "Tip: Use variable passing to carry customer context across workflow steps" },
  { text: "Tip: A/B test your proactive invite messages to see which converts better" },
  { text: "Tip: Use the inbox view to spot conversations that have gone quiet" },
  { text: "Tip: Escalation tiers let bots hand off to the right specialist automatically" },
  { text: "Tip: Video chat is available on supported channels with one toggle" },
  { text: "Tip: Sentiment scoring updates live as a conversation progresses" },
  { text: "Tip: Use IVR voice bots to handle phone support around the clock" },
  { text: "Tip: AI orchestration lets multiple bots collaborate on complex queries" },
  { text: "Tip: Version history on workflows lets you roll back to any previous save" },
  { text: "Tip: Click-to-call widgets let visitors start a phone call in one tap" },
  { text: "Tip: E-commerce integrations give your bots access to order and product data" },
  { text: "Tip: Knowledge base articles improve AI accuracy on your specific topics" },
  { text: "Tip: Global prompts let you set brand voice rules across every bot" },
  { text: "Tip: Channel overrides let the same bot behave differently on SMS vs web" },
  { text: "Tip: Page widgets can target specific URLs to show relevant offers" },
  { text: "Tip: Agent whisper mode lets supervisors guide agents without the customer seeing" },
  { text: "Tip: Use conversation summaries to hand off context between shifts without re-reading the whole thread" },
  { text: "Tip: Set a max chat limit per agent so no one gets buried and quality stays high" },
  { text: "Tip: Trigger a workflow from a keyword — route chats before an agent even touches them" },
  { text: "Tip: Custom variables let you store and reuse any customer data across the conversation" },
  { text: "Tip: Use fallback messages in your bot flows so customers never hit a dead end" },
  { text: "Tip: Connect your CRM so agents see account history the moment a chat starts" },
  { text: "Tip: Use the audit log to trace any configuration change to the person who made it" },
  { text: "Tip: Offline forms capture leads even when no agents are available" },
  { text: "Tip: Tag conversations at close to build a searchable library of resolved issues" },
  { text: "Tip: Use conditional routing to send premium customers directly to your best agents" },
  { text: "Tip: The AI knowledge base updates within 24 hours of any article change" },
  { text: "Tip: Test your IVR flow in staging before pushing it live to customers" },
  { text: "Tip: WhatsApp business profiles increase customer trust before the conversation even starts" },
  { text: "Tip: Monitor average handle time to find coaching opportunities before they become trends" },
  { text: "Tip: Set up a dedicated team for after-hours so routing never fails at night" },
  { text: "Tip: Use bot analytics to find the exact step where customers drop off most often" },
  { text: "Tip: The global prompt sets your AI's tone and boundaries for every channel at once" },
  { text: "Tip: Escalation rules let bots automatically request a human after N failed attempts" },
  { text: "Tip: Use the contact timeline to understand the full journey before joining a conversation" },
  { text: "Tip: Skill-based routing ensures the right expertise reaches every customer every time" },
  { text: "Tip: Run your bot in shadow mode first — let it suggest responses while agents approve them" },
  { text: "Tip: Page-level targeting shows proactive invites only where they convert — not everywhere" },
  { text: "Tip: IVR menus can collect intent before the bot or agent even enters the conversation" },
  { text: "Tip: Use conversation exports to train your team on real examples of great resolution" },
  { text: "Tip: Satisfaction surveys sent within 5 minutes of close get 2× higher response rates" },
  { text: "Tip: Bot handoff messages that explain the transfer improve first-impression scores" },
  { text: "Tip: Use the Operations Center during peak hours to catch queue spikes before they escalate" },
  { text: "Tip: Assign a backup team to every primary team so routing never dead-ends" },
  { text: "Tip: Knowledge base categories help the AI route questions to the most relevant articles" },
  { text: "Tip: Use conversation labels to track which agent workflows need updating most" },
  { text: "Tip: Set auto-resolve rules for conversations that go quiet after resolution" },
  { text: "Tip: Use channel-specific greetings to match tone — formal for email, casual for web chat" },
  { text: "Tip: Track first-response time by team to spot workload imbalances early" },
];

// ─── Customer service facts ───────────────────────────────────────────────────
const FACTS: WarmupMessage[] = [
  { text: "92% of customers rate live chat as the most satisfying support channel" },
  { text: "Live chat has the highest satisfaction rate of any support channel" },
  { text: "63% of millennials prefer live chat over phone or email support" },
  { text: "Proactive chat invitations have a 6× higher conversion rate than reactive chat" },
  { text: "Skilled agents can handle 3 to 5 simultaneous conversations at once" },
  { text: "Bots reduce agent workload by up to 40% on common questions" },
  { text: "First contact resolution is the single biggest driver of customer satisfaction" },
  { text: "Personalized support increases customer satisfaction scores by 20%" },
  { text: "44% of online consumers want a live agent available within 5 minutes" },
  { text: "Teams using routing rules resolve conversations 25% faster" },
  { text: "Agents who use canned responses are 35% more efficient" },
  { text: "Sentiment analysis can catch at-risk conversations before they escalate" },
  { text: "Over 60% of live chat sessions now come from mobile devices" },
  { text: "Response times under one minute increase CSAT scores by 15%" },
  { text: "The average live chat conversation is resolved in under 10 minutes" },
  { text: "79% of customers prefer live chat for quick questions" },
  { text: "Chat support costs 17–30% less to deliver than phone support" },
  { text: "Customers who use live chat are 3× more likely to make a purchase" },
  { text: "AI bots handle 60% of common customer questions without agent involvement" },
  { text: "Teams using queue alerts see 20% shorter average wait times" },
  { text: "Companies offering 24/7 support see 15% higher customer retention" },
  { text: "Agents with access to full customer history resolve tickets 40% faster" },
  { text: "Surveys sent immediately after a conversation have 3× higher response rates" },
  { text: "Teams that review CSAT weekly improve scores twice as fast" },
  { text: "Chatbots that hand off at the right moment increase satisfaction by 22%" },
  { text: "Omnichannel support teams retain 89% of customers vs 33% for single-channel" },
  { text: "Video support sessions have a 73% first-contact resolution rate" },
  { text: "Self-service options reduce ticket volume by up to 40%" },
  { text: "Customers who rate support 5 stars spend 140% more than those who rate 1 star" },
  { text: "Voice-of-the-customer programs improve retention by up to 55%" },
  { text: "Average hold time tolerance has dropped to 2 minutes for live chat" },
  { text: "Proactive support reduces inbound calls by 20–30%" },
  { text: "Agent empathy training improves CSAT scores by an average of 12 points" },
  { text: "Knowledge bases reduce inbound support volume by up to 25%" },
  { text: "73% of customers say fast resolution is what defines great support" },
  { text: "Businesses that use proactive chat see a 10–15% lift in revenue" },
  { text: "It costs 5× more to acquire a new customer than to retain an existing one" },
  { text: "A 5% increase in retention can grow profits by up to 95%" },
  { text: "86% of buyers will pay more for a better customer experience" },
  { text: "The average cost of a missed chat is a lost customer for 2 years" },
  { text: "Teams with documented processes resolve issues 30% faster" },
  { text: "66% of customers switch brands due to poor service — not price" },
  { text: "Repeat customers spend 67% more than new customers on average" },
  { text: "89% of companies now compete primarily on the basis of customer experience" },
  { text: "Customers who had a great service experience tell 9 people on average" },
  { text: "Customers who had a bad experience tell 16 people on average" },
  { text: "42% of service agents can't resolve issues efficiently due to disconnected systems" },
  { text: "Customer effort score predicts loyalty better than satisfaction score alone" },
  { text: "Reducing customer effort by 1 point improves loyalty by up to 22%" },
  { text: "AI-assisted agents resolve 40% more tickets per hour than unassisted agents" },
  { text: "80% of customer service interactions will be handled by AI by 2030" },
  { text: "Personalized service increases customer lifetime value by an average of 25%" },
  { text: "The top reason customers contact support is lack of self-service that works" },
  { text: "Proactive service prevents 20–30% of inbound support volume" },
  { text: "Chat bots resolve 69% of conversations end-to-end when trained on real data" },
  { text: "Teams that review and act on CSAT comments improve scores 2× faster than those that don't" },
  { text: "Knowledge base articles that are specific outperform generic ones 4 to 1 on deflection" },
  { text: "57% of customers prefer live chat for complex issues over any other channel" },
  { text: "74% of consumers use multiple channels before a final purchase decision" },
  { text: "Support teams using AI handle 35% more volume with the same headcount" },
  { text: "Routing accuracy improvements of 10% translate directly to 10% faster resolution times" },
  { text: "First-contact resolution rates above 75% correlate with retention rates above 90%" },
  { text: "Average chat handle time is 6.5 minutes — down from 9 minutes a decade ago" },
  { text: "Chat satisfaction scores are 12 points higher when agents use customer names" },
  { text: "Teams with documented escalation paths resolve P1 issues 50% faster" },
  { text: "Customers who resolve issues through self-service rate satisfaction 15% lower than live support" },
  { text: "Support agents have the highest employee churn rate of any knowledge-worker role" },
  { text: "Teams that give agents more autonomy on resolutions see 20% higher CSAT" },
];

// ─── Positivity & mental wellness ────────────────────────────────────────────
const POSITIVITY: WarmupMessage[] = [
  { text: "You can't control what others do — only how you choose to respond to it" },
  { text: "A three-second pause before reacting changes the outcome entirely" },
  { text: "Your focus is your most valuable asset — choose deliberately what gets it" },
  { text: "Protect your energy the same way you protect your time" },
  { text: "Other people's frustration is information, not a verdict on you" },
  { text: "Every difficult interaction is a skill you're building, not a test you're failing" },
  { text: "Setting a boundary is not unkind — it's honest and necessary" },
  { text: "Start the day with one small win and use that momentum all morning" },
  { text: "You are not required to absorb someone else's bad day" },
  { text: "Four slow breaths resets your nervous system faster than almost anything else" },
  { text: "The most effective people respond thoughtfully — they don't react impulsively" },
  { text: "A clear workspace reduces mental clutter more than most people expect" },
  { text: "Gratitude for small things builds the resilience to handle big ones" },
  { text: "Progress — not perfection — is the only reasonable goal for a working day" },
  { text: "Stepping away for five minutes often solves what an hour of staring cannot" },
  { text: "Difficult conversations handled calmly build more trust than easy ones" },
  { text: "Your calm in a stressful moment is the most powerful thing in the room" },
  { text: "Not every problem needs to be solved by you, right now, today" },
  { text: "Choosing to be kind costs nothing and pays back compoundly over time" },
  { text: "When something frustrates you, ask what it is trying to teach you" },
  { text: "Your best work comes from a settled mind, not a busy one" },
  { text: "Saying 'I don't know yet' is more honest and more powerful than a quick guess" },
  { text: "Energy flows where attention goes — be intentional about where yours points" },
  { text: "The day that seems overwhelming in the morning usually looks smaller by noon" },
  { text: "Rest is part of high performance, not the opposite of it" },
  { text: "Confidence is built one kept promise to yourself at a time" },
  { text: "You do not need to win every conversation — sometimes listening is the win" },
  { text: "What feels urgent at 9 AM rarely still feels urgent at 5 PM" },
  { text: "Your tone sets the temperature for every room you walk into" },
  { text: "Celebrate the small completions — they add up to something remarkable" },
  { text: "The most resilient people are not unaffected — they just recover faster" },
  { text: "Be the energy you would want to walk into" },
  { text: "Helping one person well today is a great day by any measure" },
  { text: "Your reaction to adversity is what others will remember about you" },
  { text: "Take pride in how you handle hard days — not just the easy ones" },
  { text: "What you practice daily is what you become eventually" },
  { text: "A gentle answer turns away frustration — tone is everything" },
  { text: "The version of yourself you're building right now is the one that handles tomorrow" },
  { text: "People forget what you said — they never forget how you made them feel" },
  { text: "Your value is not measured by your busiest day but by your most intentional one" },
  { text: "A bad moment does not have to become a bad day — the reset button is always available" },
  { text: "The habit of pausing before responding is worth more than any script" },
  { text: "Kindness under pressure is the ultimate sign of strength" },
  { text: "Tomorrow's you will thank today's you for staying calm under fire" },
  { text: "Small acts of consideration create cultures that people never want to leave" },
  { text: "Listening is not waiting for your turn to talk — it's genuinely trying to understand" },
  { text: "Focus on what you can influence and let go of what you cannot" },
  { text: "Give yourself credit for showing up, especially on the harder days" },
  { text: "High standards and self-compassion are not opposites — you need both" },
  { text: "The most successful people fail often — and choose to keep going anyway" },
  { text: "Ask yourself: will this matter in a year? That answer changes everything" },
  { text: "Your patience today becomes the customer's story about your company tomorrow" },
  { text: "The team that celebrates small wins together tends to stay together" },
  { text: "A kind word to a teammate costs nothing and often means everything" },
  { text: "Curiosity is more powerful than certainty — stay open to being surprised" },
  { text: "You bring something to this team that no one else can replicate" },
  { text: "When you help someone feel heard, you have already solved half their problem" },
  { text: "The tone you set at the start of a conversation determines most of how it ends" },
  { text: "Difficult days are not setbacks — they're the days that build your real capability" },
  { text: "What drains you most is worth examining — often a small change can protect a lot of energy" },
  { text: "The way you speak to yourself after a mistake matters as much as the mistake itself" },
  { text: "Patience is a form of confidence — it says 'I trust this will work out'" },
  { text: "A short walk between intense tasks resets focus better than pushing through" },
  { text: "People who thrive long-term are usually the ones who rest without guilt" },
  { text: "You are allowed to take a moment before answering — that pause is a sign of maturity" },
  { text: "Comparison is a trap — your only real benchmark is yesterday's version of yourself" },
  { text: "The hardest conversations, handled well, become the most trusted relationships" },
  { text: "Protecting your energy is not laziness — it's the foundation of sustainable effort" },
  { text: "Small moments of acknowledgment mean more to people than big gestures once a year" },
  { text: "Assume positive intent first — most misunderstandings dissolve when you do" },
  { text: "The strongest teams check in on each other, not just on the work" },
  { text: "Knowing when to ask for help is a skill — not a weakness" },
  { text: "You do not need to be perfect to be valuable — you need to be consistent and honest" },
  { text: "A day where you helped one person completely is never a wasted day" },
  { text: "The people who stay calm in chaos become the ones others quietly depend on" },
  { text: "Discomfort is usually the feeling of growing — not the feeling of failing" },
  { text: "The way you end a hard conversation shapes whether it stays hard" },
  { text: "Clarity is kindness — the clearer your communication, the less confusion you leave behind" },
  { text: "You are probably doing better than the voice in your head is willing to admit" },
  { text: "Every expert in any field was once a beginner who refused to give up" },
  { text: "Showing up consistently, even imperfectly, is more powerful than showing up brilliantly once" },
  { text: "Your mood doesn't have to be contagious — but your calm absolutely can be" },
  { text: "Saying 'I was wrong' quickly is a superpower most people never develop" },
  { text: "What you give attention to grows — choose what you feed your focus today" },
  { text: "The best version of you isn't more productive — it's more intentional" },
  { text: "A well-timed compliment to a colleague costs nothing and often changes their whole day" },
  { text: "You cannot pour from an empty cup — filling yours is not selfish, it's strategic" },
];

// ─── Productivity ─────────────────────────────────────────────────────────────
const PRODUCTIVITY: WarmupMessage[] = [
  { text: "If a task takes less than two minutes, do it immediately" },
  { text: "Start your day with your hardest task — it makes everything else feel easier" },
  { text: "Batch similar tasks together to eliminate the cost of context switching" },
  { text: "Time-block your calendar — unscheduled time becomes reactive time" },
  { text: "Done is better than perfect — ship it, then iterate" },
  { text: "Review your three priorities for the day before opening your messages" },
  { text: "The best system is the one you will actually use consistently every day" },
  { text: "Writing things down immediately frees your brain for thinking, not storing" },
  { text: "End every meeting with clear next steps and a named owner" },
  { text: "One focused hour beats three scattered ones every time" },
  { text: "Saying no to a good thing creates space for a great one" },
  { text: "Templates and checklists eliminate the overhead of reinventing routine tasks" },
  { text: "Review what you completed today — not just what is still left on the list" },
  { text: "The five-minute rule: if you're avoiding something, just start for five minutes" },
  { text: "Energy management matters as much as time management" },
  { text: "Ask yourself: is this the highest-value use of my next hour?" },
  { text: "A weekly review catches what daily work consistently misses" },
  { text: "Clarity on priorities makes every decision noticeably faster" },
  { text: "Automation handles the repetitive so your team focuses on what matters" },
  { text: "The best support teams document everything so nothing is lost in transition" },
  { text: "Measure what you want to improve — then actually review it regularly" },
  { text: "Short daily standups prevent long weekly problems from forming" },
  { text: "Small process improvements compound dramatically over months" },
  { text: "A clean handoff between shifts is the mark of a high-performing team" },
  { text: "Data without a decision attached is just noise" },
  { text: "A strong morning routine removes decisions when willpower is naturally lowest" },
  { text: "The fastest way to unblock a project is often a five-minute conversation" },
  { text: "Automate anything you do more than once a week" },
  { text: "Build the habit first — optimize it after it is stable" },
  { text: "Great documentation is the best gift you can give your future self" },
  { text: "Schedule time to think — it is real work, not a luxury" },
  { text: "Work expands to fill the time you give it — set a deadline even for yourself" },
  { text: "Track three wins every week — the pattern shows you what to do more of" },
  { text: "Consistent beats intense — show up every day and let the gains compound" },
  { text: "The most productive people are ruthless about what they deprioritize" },
  { text: "Decisions made slowly are often made twice — build a framework and trust it" },
  { text: "If two people are doing the same work, that is a process problem to fix" },
  { text: "The 80/20 rule applies everywhere — find the 20% that drives 80% of your results" },
  { text: "A clear 'done' definition prevents tasks from dragging on indefinitely" },
  { text: "Minimize meetings — protect deep work blocks like they're appointments" },
  { text: "Every recurring problem is a system to fix, not a fire to fight" },
  { text: "Close browser tabs you're 'saving for later' — you won't go back to them" },
  { text: "Teach your process to someone else — you'll find the gaps immediately" },
  { text: "Inbox zero is less about zero emails and more about zero open decisions" },
  { text: "The best productivity hack is saying no to things that don't move the needle" },
  { text: "A 15-minute planning session saves hours of reactive scrambling" },
  { text: "Document decisions when they're made — not after people forget why" },
  { text: "A good checklist eliminates the cognitive overhead of remembering every step" },
  { text: "If you can delegate something to 80% of your quality, delegate it and move on" },
  { text: "The best teams have fewer meetings because they have better async communication" },
  { text: "Before starting a task, write down what 'done' looks like — clarity before effort" },
  { text: "Batch your email and message checking — reactive reading destroys deep work" },
  { text: "A daily shutdown ritual helps your brain leave work at work" },
  { text: "Write tomorrow's priority list today — future you will thank you for it" },
  { text: "Context-switching costs 20 minutes of focus each time — protect your unbroken blocks" },
  { text: "The most scalable thing you can do is teach your process, not just do the work" },
  { text: "Clear labels and naming conventions save hours of confusion downstream" },
  { text: "Small, daily investments in documentation pay dividends for years" },
  { text: "Ask 'what would make this easier?' before diving in — simple changes often exist" },
  { text: "Track your energy levels for a week — you'll find a clear pattern worth protecting" },
  { text: "The real cost of a bad habit is not the habit itself — it's what it crowds out" },
  { text: "Good defaults remove the need for willpower — design your environment to help you" },
  { text: "Revisit your recurring tasks regularly — some of them can just be stopped entirely" },
  { text: "The fastest way to get unstuck is to talk it through with someone else out loud" },
  { text: "Speed of decision matters more than perfection of decision in most situations" },
  { text: "Ship, learn, iterate — the cycle beats planning in isolation every time" },
  { text: "Protect the first 90 minutes of your day like they're the most valuable hours — because they are" },
  { text: "When in doubt, do the scariest task first — everything after it feels manageable" },
  { text: "Good judgment is knowing when to break the rule, not just knowing the rule" },
  { text: "The best teams celebrate both wins AND lessons — safety to fail fast is a competitive advantage" },
  { text: "Simplicity is a feature — every unnecessary complexity is a future maintenance cost" },
];

// ─── Interesting life & world facts ──────────────────────────────────────────
const LIFE_FACTS: WarmupMessage[] = [
  { text: "Honey never spoils — archaeologists found 3,000-year-old honey still edible" },
  { text: "Octopuses have three hearts and blue blood" },
  { text: "Your body produces a completely new skeleton roughly every 10 years" },
  { text: "Bananas are curved because they grow toward the sun" },
  { text: "A single cloud can weigh more than a million pounds" },
  { text: "Light from the sun takes about 8 minutes to reach Earth" },
  { text: "Sharks are older than trees — they've existed for 450 million years" },
  { text: "The average person walks 100,000 miles in their lifetime" },
  { text: "Ants can carry 50 times their own body weight" },
  { text: "Your brain uses 20% of the body's energy despite being only 2% of its weight" },
  { text: "A strawberry isn't a berry, but a banana technically is" },
  { text: "Laughter is contagious because humans are wired to mirror others' emotions" },
  { text: "Oxford University is older than the Aztec Empire" },
  { text: "The Eiffel Tower grows 6 inches taller in summer due to thermal expansion" },
  { text: "Crows can recognize and remember individual human faces for years" },
  { text: "Your heart generates enough pressure to squirt blood 30 feet" },
  { text: "Dolphins sleep with one eye open — half the brain stays awake" },
  { text: "Humans share 60% of their DNA with bananas" },
  { text: "Rain has a smell — petrichor — caused by a compound called geosmin" },
  { text: "Sea otters hold hands while sleeping so they don't drift apart" },
  { text: "The mantis shrimp can punch with the force of a bullet" },
  { text: "Hot water can freeze faster than cold water in some conditions — the Mpemba effect" },
  { text: "Trees communicate through underground fungal networks" },
  { text: "The Moon is moving away from Earth by about 1.5 inches per year" },
  { text: "Butterflies taste with their feet" },
  { text: "Saturn is less dense than water — it would float in a large enough ocean" },
  { text: "Flamingos are white — their pink color comes entirely from their food" },
  { text: "Humans are the only animals that blush" },
  { text: "Wombats produce cube-shaped droppings — the only animal known to do so" },
  { text: "Lightning strikes Earth about 100 times every second" },
  { text: "Your body has about 60,000 miles of blood vessels" },
  { text: "A spider's silk strand is stronger than steel of the same diameter" },
  { text: "Mount Olympus on Mars is nearly three times taller than Mount Everest" },
  { text: "Babies are born with 270 bones — adults end up with 206 as many fuse" },
  { text: "Polar bears' fur is transparent, not white — it scatters light" },
  { text: "Cows have best friends and produce more milk when near their favorite companion" },
  { text: "DNA in your body, stretched end to end, would reach the sun and back 600 times" },
  { text: "Your heart beats about 100,000 times a day and 35 million times a year" },
  { text: "A caterpillar essentially dissolves into soup inside its cocoon before becoming a butterfly" },
  { text: "Identical twins don't have the same fingerprints — they're shaped by fetal movement" },
  { text: "Crows can hold grudges and teach other crows to dislike specific humans" },
  { text: "Humans are the only species that cooks food — which dramatically shaped our evolution" },
  { text: "Earth is the only planet in our solar system not named after a god" },
  { text: "The word 'salary' comes from the Latin for salt — Romans were sometimes paid in salt" },
  { text: "The ocean produces over 50% of Earth's oxygen — mostly from phytoplankton" },
  { text: "Running and walking a mile burn nearly the same number of calories" },
  { text: "The first computer bug was a real bug — a moth found in a Harvard relay in 1947" },
  { text: "Raindrops are shaped like hamburger buns, not teardrops, due to air resistance" },
  { text: "The color blue was one of the last colors to be named in most ancient languages" },
  { text: "Squirrels forget about half their buried nuts — accidentally planting millions of trees" },
  { text: "Your fingernails grow about 3.5mm per month — toenails about half as fast" },
  { text: "The skin is the human body's largest organ" },
  { text: "There are more species of beetles on Earth than any other animal — over 400,000" },
  { text: "A single mature tree can absorb 48 pounds of CO₂ per year" },
  { text: "There are more trees on Earth than stars in the Milky Way" },
  { text: "Babies laugh about 300 times per day — adults average around 20" },
  { text: "Orcas (killer whales) are actually the largest member of the dolphin family" },
  { text: "Your brain is roughly 73% water — dehydration directly impairs thinking" },
  { text: "Music activates more areas of the brain simultaneously than any other activity" },
  { text: "The sense of touch is the first sense to develop in the womb" },
  { text: "Glass catfish are nearly transparent — you can see their organs through their bodies" },
  { text: "Astronauts grow up to 2 inches taller in space due to spinal decompression" },
  { text: "Penguins propose to their mates with a carefully chosen pebble" },
  { text: "The Great Barrier Reef is the largest living structure on Earth, visible from space" },
  { text: "There are 118 elements in the periodic table — 98 occur naturally" },
  { text: "The universe is approximately 13.8 billion years old" },
  { text: "You share DNA with every living organism on Earth" },
  { text: "Cleopatra lived closer in time to the Moon landing than to the Great Pyramid's construction" },
  { text: "A group of flamingos is called a flamboyance" },
  { text: "The platypus is one of the few venomous mammals" },
  { text: "Newborns don't have kneecaps — they develop over the first few years of life" },
  { text: "The longest recorded hiccup lasted 68 years — Charles Osborne, 1922 to 1990" },
  { text: "There are 10 times more bacterial cells in your body than human cells" },
  { text: "Your left lung is smaller than your right — it makes room for your heart" },
  { text: "An apple, potato, and onion taste nearly identical when eaten with your nose plugged" },
  { text: "Roosters have a built-in mechanism that partially deafens their own crow" },
  { text: "Starfish have no brain and no blood — they use seawater as their circulatory fluid" },
  { text: "Baby blue whales gain about 200 pounds per day in their first year" },
  { text: "Your bones are roughly 4 times stronger than concrete by weight" },
  { text: "There is a jellyfish (Turritopsis dohrnii) that is biologically immortal" },
  { text: "Elephants are the only animals known to hold ceremonies around their dead" },
  { text: "Goldfish have a memory span well over 3 months — the '3-second' myth is false" },
  { text: "Hummingbirds are the only birds that can fly backward" },
  { text: "There are more microorganisms in a teaspoon of soil than humans on Earth" },
  { text: "The human body is bioluminescent — we glow faintly, just beyond visible range" },
  { text: "Wolves howl in different dialects depending on where they're from" },
  { text: "Plants respond to the sound of running water by growing toward it" },
  { text: "Coral reefs cover less than 1% of the ocean floor but support 25% of all marine life" },
  { text: "Lobsters were once considered a food of the poor — fed to prisoners in colonial America" },
  { text: "A group of owls is called a parliament" },
  { text: "Stars don't actually twinkle — it's Earth's atmosphere distorting the light" },
  { text: "Whales sing to each other across hundreds of miles of ocean" },
  { text: "The original Ferris wheel was built for the 1893 World's Fair in Chicago" },
  { text: "The Mona Lisa has no eyebrows — they were fashionable to shave in Renaissance Florence" },
  { text: "Beethoven composed some of his greatest works while completely deaf" },
  { text: "Honey bees must visit about 2 million flowers to make one pound of honey" },
  { text: "There are more public libraries in the US than McDonald's restaurants" },
  { text: "The Great Wall of China is not visible from space with the naked eye" },
  { text: "A group of ravens is called a conspiracy — or sometimes an unkindness" },
  { text: "10% of all photos ever taken were taken in the last 12 months" },
  { text: "There are more stars in the universe than grains of sand on all of Earth's beaches" },
  { text: "Ice cream was invented in China before it reached Europe" },
  { text: "A group of butterflies is called a kaleidoscope" },
  { text: "Each human ear contains over 30,000 hair cells that detect sound" },
  { text: "A bolt of lightning is 5 times hotter than the surface of the sun" },
  { text: "The average human dream lasts only 20–30 minutes" },
  { text: "The human nose can detect more than 1 trillion different scents" },
  { text: "Your brain's storage capacity is effectively unlimited — roughly 2.5 petabytes" },
  { text: "Octopuses can edit their own RNA to adapt to changing temperatures" },
  { text: "A day on Venus is longer than a year on Venus" },
  { text: "Every second, about 100 lightning bolts strike the Earth" },
  { text: "Sunflowers track the sun across the sky — a process called heliotropism" },
  { text: "Spiders eat more insects by weight each year than the entire human meat intake" },
  { text: "The word 'muscle' comes from the Latin for 'little mouse' — a flexed bicep looks like one" },
  { text: "Saturn's moon Titan has rain — but it rains methane, not water" },
  { text: "There are more possible chess games than atoms in the observable universe" },
  { text: "The shortest scheduled flight is 57 seconds, from Westray to Papa Westray, Scotland" },
  { text: "Every 60 seconds, 1,800 thunderstorms occur somewhere on Earth" },
  { text: "Your eyes can distinguish approximately 10 million different colors" },
  { text: "A group of hedgehogs is called a prickle" },
  { text: "The human body sheds about 9 pounds of skin cells every year" },
  { text: "You blink about 15–20 times per minute, yet rarely notice yourself doing it" },
  { text: "Elephants are the only animals that can't jump" },
  { text: "Crocodiles haven't changed significantly in 200 million years" },
  { text: "The average lifespan of a taste bud is only 10 days" },
  { text: "Trees in urban areas live an average of 13 years — rural trees live hundreds" },
  { text: "A single raindrop falling on dry soil releases up to 2,000 tiny droplets on impact" },
  { text: "The color of the sky at sunset tells you about the dust and moisture in the air" },
  { text: "A group of cats is called a clowder; a group of kittens is called a kindle" },
  { text: "Ants don't sleep — they take hundreds of 1-minute power naps throughout the day" },
  { text: "The human eye moves about 200 degrees per second — one of the body's fastest motions" },
  { text: "Dogs can detect seizures before they happen by sensing biochemical changes" },
  { text: "A cloud of gas in the constellation Sagittarius smells like raspberries" },
  { text: "The world's oldest living tree is over 5,000 years old" },
  { text: "Your skin completely renews itself every 2–4 weeks" },
  { text: "The average person forgets 50% of new information within an hour of learning it" },
  { text: "Humans are one of the few species capable of recognizing themselves in a mirror" },
  { text: "There are more possible iterations of a shuffled deck of cards than atoms in Earth" },
  { text: "A group of meerkats is called a mob or a gang" },
  { text: "The human circulatory system, laid end to end, would wrap around Earth twice" },
  { text: "The sense of smell is the sense most strongly linked to memory" },
  { text: "Typewriter uses only the top row of keys on a QWERTY keyboard" },
  { text: "Trees can send chemical distress signals to each other when under insect attack" },
  { text: "There are more than 1 million described insect species — millions more undiscovered" },
  { text: "The original meaning of 'nice' was foolish or silly — language evolves dramatically" },
  { text: "Coral polyps build massive reefs from their own calcium carbonate skeletons" },
  { text: "Iguanas can hold their breath for up to 45 minutes underwater" },
  { text: "The fingerprints of koalas are nearly indistinguishable from human fingerprints" },
  { text: "A group of porcupines is also called a prickle" },
  { text: "The first words ever spoken on the telephone: 'Mr. Watson, come here — I want to see you'" },
  { text: "Honey's antibacterial properties were so reliable it was used on wounds in WWI" },
  { text: "Rainforest trees 'talk' by releasing chemical signals into the air" },
  { text: "Your brain is more active at night than during the day" },
  { text: "The longest English word typeable with only the left hand is 'stewardesses'" },
  { text: "More people die annually from falling coconuts than from shark attacks" },
  { text: "A group of gorillas is called a band or a troop" },
  { text: "Every human on Earth descended from a common maternal ancestor about 200,000 years ago" },
  { text: "Crows make and use tools — they'll shape sticks to reach food in a hole" },
  { text: "The universe is expanding, and the most distant galaxies are receding faster than light" },
  { text: "A snail can sleep for up to 3 years during extreme drought" },
  { text: "There are roughly 8 million lightning strikes on Earth every day" },
  { text: "The average human sneeze travels at about 100 miles per hour" },
  { text: "Muscles are the last organ to stop functioning after death" },
  { text: "The human body produces about 25,000 quarts of saliva in a lifetime" },
  { text: "A group of jellyfish is called a bloom or a smack" },
  { text: "The first video game was created in 1958 — a tennis simulation on an oscilloscope" },
  { text: "Every atom in your body was forged inside a star billions of years ago" },
  { text: "Sea otters use rocks as tools — and often have a 'favorite' rock they keep" },
  { text: "The Amazon rainforest produces 20% of the world's oxygen" },
  { text: "Bees can recognize human faces — they use the same feature-assembly method we do" },
  { text: "There are more than 40,000 distinct species of spiders on Earth" },
  { text: "The human brain generates about 70,000 thoughts per day" },
  { text: "A day on Mars is 24 hours 37 minutes — almost identical to Earth's" },
  { text: "An eagle's eyesight is 4–8 times stronger than a human's" },
  { text: "Your heart can continue beating outside your body as long as it gets oxygen" },
  { text: "The number of possible positions in a chess game exceeds 10 to the power of 120" },
  { text: "Giraffes have the same number of neck vertebrae as humans — just much larger ones" },
  { text: "Blind people dream — just with other senses rather than images" },
  { text: "The Sahara Desert was green and lush as recently as 5,000 years ago" },
  { text: "Pigeons can recognize themselves in mirrors and distinguish individual humans" },
  { text: "A blue whale's heartbeat is so loud it can be detected from 2 miles away" },
  { text: "The average cloud travels at 30–40 mph and can weigh 500 million pounds" },
  { text: "You have a unique tongue print — just like your fingerprints" },
  { text: "The world's largest known organism is a honey fungus in Oregon, covering 2,385 acres" },
  { text: "Parrots don't just mimic — some demonstrate actual understanding of words and concepts" },
  { text: "Lake Baikal contains 20% of all the world's unfrozen fresh surface water" },
  { text: "Ancient Romans used crushed mouse brains as toothpaste" },
  { text: "The letters in 'listen' are an anagram of 'silent'" },
  { text: "Your lungs have a surface area roughly the size of a tennis court" },
  { text: "Cats have a third eyelid, called a nictitating membrane, rarely seen in healthy cats" },
  { text: "A group of hippos is called a bloat" },
  { text: "Sharks have been relatively unchanged for 450 million years — before the first trees" },
  { text: "The Pacific Ocean is wider than the Moon is from Earth" },
  { text: "There is enough iron in your body to make a small nail" },
  { text: "The world's quietest room absorbs 99.99% of sound — people hallucinate in it within minutes" },
  { text: "Every continent on Earth ends in the same letter it starts with" },
  { text: "Sloths can hold their breath longer than dolphins — up to 40 minutes" },
  { text: "Your stomach gets a new lining every 3–4 days to prevent it from digesting itself" },
  { text: "A group of crows is called a murder" },
  { text: "Elephants are the only animals known to show empathy for dying members of other species" },
  { text: "The shortest war in history lasted 38 minutes — the Anglo-Zanzibar War of 1896" },
  { text: "A shark must keep swimming or water won't pass through its gills — most will drown if still" },
  { text: "Mantis shrimps can see 16 types of color receptors — humans have only 3" },
  { text: "The dead star 'Lucy' in Canis Major is a diamond about the size of Earth" },
  { text: "Most of the universe (about 95%) is made of dark matter and dark energy we can't see" },
  { text: "You are physically unable to hum while holding your nose" },
  { text: "A group of ferrets is called a business" },
  { text: "Tardigrades (water bears) can survive the vacuum of space, radiation, and boiling water" },
  { text: "The average American walks about 3,000–4,000 steps per day — far below the recommended 10,000" },
  { text: "You can hear your heartbeat in your ears when you press them against a pillow" },
  { text: "Honeybees can recognize faces and remember them for days" },
  { text: "The world's oldest joke was written in Sumeria around 1900 BC — it's about a shoe" },
  { text: "Paper was invented in China around 105 AD — books existed for 3,000 years before that" },
  { text: "The word 'quarantine' comes from 40 days — 'quarantina' in Italian" },
  { text: "An ostrich's eye is bigger than its brain" },
  { text: "Neutron stars are so dense that a teaspoon of one weighs about 6 billion tons" },
  { text: "A woodpecker's tongue wraps around its skull to cushion its brain during pecking" },
  { text: "The driest place on Earth is Antarctica's Dry Valleys — no rain for 2 million years" },
  { text: "Pistol shrimp snap their claws so fast they create a bubble hotter than the sun's surface" },
  { text: "The total length of all your body's neurons, end to end, would reach the Moon and back" },
  { text: "Glass is technically not a solid or a liquid — it's an amorphous solid" },
  { text: "The Voyager 1 spacecraft is over 15 billion miles from Earth and still transmitting data" },
  { text: "An average cumulonimbus thunderstorm cloud contains the energy of 10 atomic bombs" },
  { text: "Axolotls can regenerate entire limbs, spinal cords, hearts, and parts of their brains" },
  { text: "Astronauts' fingernails often fall off in space due to pressure in spacesuit gloves" },
  { text: "The human brain can process an image in as little as 13 milliseconds" },
  { text: "The oldest known living organism is a seagrass meadow in Australia, over 100,000 years old" },
  { text: "Sound travels about 4.3 times faster in water than in air" },
  { text: "A cubic inch of human bone can bear a load of 19,000 pounds — roughly 4 pickup trucks" },
  { text: "The electric eel can produce shocks of up to 860 volts — enough to stun a horse" },
  { text: "Bioluminescent organisms in the ocean produce more light than all human-made sources combined" },
  { text: "The longest mountain range on Earth is underwater — the Mid-Atlantic Ridge stretches 40,000 miles" },
  { text: "Chameleons don't change color for camouflage — they do it to communicate mood and temperature" },
  { text: "Your DNA contains enough information to stretch from the Earth to Pluto and back — 17 times" },
  { text: "Honey badgers can withstand venomous snake bites that would kill most animals" },
  { text: "The human body contains enough carbon to make about 9,000 pencils" },
  { text: "Glaciers store about 69% of the world's fresh water" },
  { text: "A photon of light takes 8 minutes to reach Earth but 100,000 years to leave the sun's core" },
  { text: "The tongue of a blue whale weighs as much as an elephant" },
  { text: "The mimic octopus can impersonate over 15 different marine species to avoid predators" },
  { text: "Octopuses have copper-based blood — it turns blue when oxygenated" },
  { text: "The coldest temperature ever recorded on Earth was minus 128.6 degrees Fahrenheit in Antarctica" },
  { text: "A hummingbird's heart beats more than 1,200 times per minute during flight" },
];

// ─── Motivational ─────────────────────────────────────────────────────────────
const MOTIVATIONAL: WarmupMessage[] = [
  { text: "Every great conversation starts with a great team" },
  { text: "Building connections, one chat at a time" },
  { text: "Great support starts right here" },
  { text: "Another great day of work starts now" },
  { text: "Powering better customer experiences" },
  { text: "Every message is an opportunity to make someone's day better" },
  { text: "Making support smarter, one interaction at a time" },
  { text: "Where great teams deliver great experiences" },
  { text: "Built for teams who genuinely care about people" },
  { text: "The right tools make great teams even better" },
  { text: "Your configuration shapes the experience every customer has" },
  { text: "Small improvements every day become transformational over time" },
  { text: "The work you do here ripples out to thousands of customers" },
  { text: "Great teams are not born — they are built through small daily commitments" },
  { text: "Today is a good day to help someone solve their problem" },
  { text: "Customer experience is the competitive advantage that cannot be copied" },
  { text: "The best support teams never stop asking how they can get better" },
  { text: "Excellence in support is a habit — and habits are built by showing up" },
  { text: "The best time to improve a process was yesterday — the second best is right now" },
  { text: "Customers remember how you fixed the problem more than the problem itself" },
  { text: "A team that communicates well can outperform a team twice its size" },
  { text: "Behind every great customer experience is a team that refuses to settle" },
  { text: "Consistency builds trust — show up the same way every time" },
  { text: "The difference between good and great is one more question asked" },
  { text: "Your tools are only as good as the people who use them intentionally" },
  { text: "Start each interaction like it's the customer's first — because it might be" },
  { text: "What you measure improves — what you celebrate improves faster" },
  { text: "Every resolved conversation is proof that your team made someone's day better" },
];

// Fisher-Yates shuffle — runs once at module load so every session gets
// a different order, but the loading messages always come first.
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Loading messages stay at the front so they appear while the app is
// actually loading. Everything else is shuffled into a random order.
//
// Weighting: positivity, platform tips, call-center facts, and productivity
// each appear 3× as often as life facts (1×) and motivational (1×).
// This creates a warm, useful experience weighted toward growth and service quality.
export const WARMUP_MESSAGES: WarmupMessage[] = [
  ...LOADING,
  ...shuffle([
    ...PLATFORM_TIPS,   // 3× weight
    ...PLATFORM_TIPS,
    ...PLATFORM_TIPS,
    ...FACTS,           // 3× weight — call-center & customer service facts
    ...FACTS,
    ...FACTS,
    ...POSITIVITY,      // 3× weight — mental wellness & resilience
    ...POSITIVITY,
    ...POSITIVITY,
    ...PRODUCTIVITY,    // 3× weight — productivity & work habits
    ...PRODUCTIVITY,
    ...PRODUCTIVITY,
    ...LIFE_FACTS,      // 1× — interesting life/science/world facts
    ...MOTIVATIONAL,    // 1× — brief inspirational
  ]),
];

export const LOADING_COUNT = LOADING.length;
